import { GroupUserItemProps } from "./GroupUserItemProps";
import { styled } from "@mui/material";
import { Typography } from "@kaltura/ds-react-components";
import { HtmlReactParser, WebcastHelper, WebcastStatus } from "@kaltura/mediaspace-shared-utils";
import { useButtonAnalytics, useCurrentTimeForAnimation } from "@kaltura/mediaspace-shared-hooks";
import { UserName, UserNameSkeleton, UserThumbnail, UserThumbnailSkeleton } from "@kaltura/mediaspace-shared-user";
import { Skeleton } from "@kaltura/mediaspace-shared-styled";
import { TruncateWithTooltip } from "@kaltura/mediaspace-shared-ui";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { VisibleFocusStyle } from "@kaltura/ds-react-theme";

export interface GroupUserGridItemProps extends GroupUserItemProps {
    onClick?: () => void;
    analyticsAction?: string;
    onThumbnailResize?: (height: number) => void;
    centerText?: boolean;
    tabIndex?: number;
}

export const GroupUserGridItem = ({
    item,
    sessions,
    onClick,
    analyticsAction,
    onThumbnailResize,
    centerText = false,
    tabIndex = 0,
}: GroupUserGridItemProps) => {
    // Refresh the component each second to display live status according to the current time
    useCurrentTimeForAnimation(undefined, 1000);

    const isLive = sessions.some(
        (schedulingData) => WebcastHelper.getLiveStatus(schedulingData) === WebcastStatus.live
    );

    const sendButtonAnalytics = useButtonAnalytics();
    const baseClassName = "kms-ds-group-page-user-grid-item";
    return (
        <StyledButtonWrapper
            onClick={() => {
                sendButtonAnalytics(analyticsAction ?? "Open user modal box", ButtonClickAnalyticsType.OPEN);
                onClick?.();
            }}
            className={`${baseClassName}-link`}
            tabIndex={tabIndex}
            centerText={centerText}
        >
            <UserThumbnail {...item} onThumbnailResize={onThumbnailResize} />

            <StyledName
                component={"h3"}
                name={item.name}
                isLive={isLive}
                centerText={centerText}
                className={`${baseClassName}-name`}
                lines={2}
            />

            {item.title && (
                <TruncateWithTooltip lines={3} text={HtmlReactParser(item.title)}>
                    <StyledTitle variant={"body1"} centerText={centerText} className={`${baseClassName}-title`}>
                        {HtmlReactParser(item.title)}
                    </StyledTitle>
                </TruncateWithTooltip>
            )}
        </StyledButtonWrapper>
    );
};

export const GroupUserGridItemSkeleton = () => {
    return (
        <div>
            <UserThumbnailSkeleton />
            <StyledNameSkeleton variant={"rectangular"} />
            <StyledTitleSkeleton variant={"rectangular"} />
        </div>
    );
};

const StyledName = styled(
    UserName,
    {
        shouldForwardProp(propName: PropertyKey): boolean {
            return propName !== "centerText";
        },
    }
)<{ centerText?: boolean }>(({ theme, centerText }) => ({
    marginTop: theme.spacing(2),
    ...(
        centerText && {
            justifyContent: "center",
        }
    ),
}));

const StyledNameSkeleton = styled(UserNameSkeleton)(({ theme }) => ({
    marginTop: theme.spacing(2),
}));

const StyledTitle = styled(
    Typography,
    {
        shouldForwardProp(propName: PropertyKey): boolean {
            return propName !== "centerText";
        },
    }
)<{ centerText?: boolean }>(({ theme, centerText }) => ({
    marginTop: theme.spacing(1),
    color: theme.kaltura.palette.tone2,
    ...(
        centerText && {
            textAlign: "center",
        }
    ),
    whiteSpace: "normal",
}));

const StyledTitleSkeleton = styled(Skeleton)(({ theme }) => ({
    height: theme.spacing(2),
    borderRadius: theme.kaltura.shape.roundness1,
    marginTop: theme.spacing(1),
}));

const StyledButtonWrapper = styled(
    "button",
    {
        shouldForwardProp(propName: PropertyKey): boolean {
            return propName !== "centerText";
        },
    }
)<{ centerText?: boolean }>(({ centerText }) => ({
    display: "block",
    background: "none",
    border: "none",
    padding: 0,
    margin: 0,
    cursor: "pointer",
    height: "0%",
    width: "100%",
    "&:focus-visible": VisibleFocusStyle,
    textAlign: centerText ? "center" : "unset",
}));
